import { ReactElement, useCallback } from "react";
import {
	SingleSignOnConfigurationMode,
	SingleSignOnFormData
} from "client/Features/Settings/SsoSettings/SingleSignOnSettingsControl";
import { IFormHandler } from "client/Forms";
import {
	CreateOAuth2ApplicationInAzureModel,
	CustomSignOutRedirectUrlActionType,
	OAuth2ConfigurationSaveModel,
	SignOutRedirectUrlConfiguration,
	SingleSignOnSettingsViewModel,
	SingleSignOnType,
	WsFederationConfigurationSaveModel,
	configureOAuth2SingleSignOn,
	configureWsFederationSingleSignOn,
	createOAuth2AzureApplication,
	getUrlForOAuth2ConfigurationVerification
} from "client/Api/SingleSignOnSettings/singleSignOnSettings";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import * as windowFactory from "client_legacy/ndaw/windowFactory";
import { localize } from "client_legacy/ndaw/culture";
import {
	IRootValidation,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import { never } from "client_legacy/ndaw/errors";
import { IValueInputAttachable } from "client/Forms/attachable";
import { convertServerErrors } from "client/Validation/serverErrors";
import { ClientValidationErrorModel } from "client/Api/common";
import { HttpRequestError } from "client/Api/httpClient";
import { useSsoApplicationCreatedDialog } from "./SsoApplicationCreatedDialog";
import {
	useEnableMicrosoftAuthenticatorMobileSignInDialog
} from "./EnableMicrosoftAuthenticatorMobileSignInDialog";

export function useSingleSignOnSettingsForm(
	formHandler: IFormHandler<SingleSignOnFormData>,
	initialFormData: SingleSignOnFormData,
	isBusy: boolean,
	onIsBusyChange: (value: boolean) => void
): [
	submitHandler: () => Promise<TryResult<SingleSignOnType>>,
	verifyOAuth2Handler: () => void,
	enableMicrosoftAuthenticatorMobileSignInHandler: () => void,
	hasUnsavedOAuth2CredentialsChanges: boolean,
	dialogElement: ReactElement
] {
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const [ssoApplicationCreatedDialog, openSsoApplicationCreatedDialog] = useSsoApplicationCreatedDialog();
	const [
		enableMicrosoftAuthenticatorMobileSignInDialog,
		openEnableMicrosoftAuthenticatorMobileSignInDialog
	] = useEnableMicrosoftAuthenticatorMobileSignInDialog();

	const formData: SingleSignOnFormData = formHandler.value;

	const {
		authority: initialAuthority,
		clientId: initialClientId,
	} = initialFormData.oauth2
		.manual;

	const {
		authority,
		clientId,
		key,
	} = formData.oauth2
		.manual;

	const hasUnsavedOAuth2CredentialsChanges: boolean = authority !== initialAuthority
		|| clientId !== initialClientId
		|| key.containsNewValue;

	const tryProcessServerError = useCallback(
		(error: unknown, prefix: string): boolean => {
			if (!(error instanceof HttpRequestError && error.statusCode === 400)) {
				return false;
			}

			try {
				let errors = error.readJson<ClientValidationErrorModel[] | IUserFriendlyExceptionMessage>();
				if (!Array.isArray(errors)) {
					errors = [
						{
							propertyName: "",
							errorMessage: errors.message
						}
					];
				}

				errors = errors.map(
					(x): ClientValidationErrorModel => {
						return {
							...x,
							propertyName: x.propertyName
								? prefix + x.propertyName
								: x.propertyName
						};
					}
				);

				formHandler.validation
					.setServerErrors(
						convertServerErrors(errors)
					);

				return true;
			} catch (e) {
				console.error(e);
				return false;
			}
		},
		[formHandler.validation]
	);

	const verifyOAuth2Handler = useCallback(
		async (): Promise<void> => {
			const validation: IRootValidation<SingleSignOnFormData> = formHandler.validation;

			onIsBusyChange(true);

			try {

				const model: OAuth2ConfigurationSaveModel = createOAuth2ConfigurationSaveModel(formData);

				validation.clearServerErrors();

				const { url } = await getUrlForOAuth2ConfigurationVerification(model, globalCancellation);

				// Can't use the Window component (iframe) due to "X-Frame-Options: DENY"
				windowFactory.open(url);

				const wasVerifyClickedAtleastOnceAttachable: IValueInputAttachable<boolean> = formHandler
					.for("oauth2")
					.for("manual")
					.for("wasVerifyClickedAtleastOnce");

				wasVerifyClickedAtleastOnceAttachable.reducer
					.setValue(true);

				wasVerifyClickedAtleastOnceAttachable.validation
					.triggerInputFinished();

				onIsBusyChange(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!tryProcessServerError(e, "oauth2.manual.")) {
					alertManager.showError(localize("_FailedToRetrieveTheOAuth2AuthorizeEndpoint"));
				}

				onIsBusyChange(false);
			}
		},
		[
			alertManager,
			formData,
			formHandler,
			globalCancellation,
			onIsBusyChange,
			tryProcessServerError
		]

	);

	const enableMicrosoftAuthenticatorMobileSignInHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			const result: TryResult<void> = await openEnableMicrosoftAuthenticatorMobileSignInDialog();

			if (!result.wasSuccessful) {
				return;
			}

			const isEnabledAttachable: IValueInputAttachable<boolean> = formHandler
				.for("oauth2")
				.for("manual")
				.for("isMicrosoftAuthenticatorMobileSignInEnabled");

			isEnabledAttachable.reducer
				.setValue(true);

			isEnabledAttachable.validation
				.triggerInputFinished();

			alertManager.showSuccess(localize("_SuccessfullyEnabledMicrosoftAuthenticatorSignIn"));
		},
		[
			alertManager,
			formHandler,
			isBusy,
			openEnableMicrosoftAuthenticatorMobileSignInDialog
		]
	);

	const submitHandler = useCallback(
		async (): Promise<TryResult<SingleSignOnType>> => {
			if (isBusy) {
				return { wasSuccessful: false };
			}

			onIsBusyChange(true);

			const validation: IRootValidation<SingleSignOnFormData> = formHandler.validation;
			let validationMessagesPrefix: string | undefined;
			let unhandledValidationMessageKey: LocalizationKey = "_FailedToSaveTheSingleSignOnSettings";

			try {
				const validationResult: InputValidationResult = await validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					onIsBusyChange(false);
					return { wasSuccessful: false };
				}

				switch (formData.singleSignOnType) {
					case SingleSignOnType.OAuth2:
						switch (formData.oauth2.mode) {
							case SingleSignOnConfigurationMode.Automatic:
								{
									validationMessagesPrefix = "oauth2.automatic.";
									const model: CreateOAuth2ApplicationInAzureModel = createOAuth2AzureApplicationCreationModel(formData);
									await createOAuth2AzureApplication(model);
									await openSsoApplicationCreatedDialog();

									return {
										wasSuccessful: true,
										result: SingleSignOnType.OAuth2
									};
								}
							case SingleSignOnConfigurationMode.Manual:
								{
									validationMessagesPrefix = "oauth2.manual.";
									unhandledValidationMessageKey = "_WeWereUnableToSetupYourAzureADApplicationPleaseTryManually";
									const model: OAuth2ConfigurationSaveModel = createOAuth2ConfigurationSaveModel(formData);
									await configureOAuth2SingleSignOn(model, globalCancellation);

									return {
										wasSuccessful: true,
										result: SingleSignOnType.OAuth2
									};
								}
							default:
								return never(formData.oauth2.mode);
						}
					case SingleSignOnType.WsFederation:
						validationMessagesPrefix = "oauth2.wsFed.";
						const model: WsFederationConfigurationSaveModel = createWsFederationConfigurationSaveModel(formData);
						await configureWsFederationSingleSignOn(model, globalCancellation);

						return {
							wasSuccessful: true,
							result: SingleSignOnType.WsFederation
						};
					default:
						never(formData.singleSignOnType);
				}
			} catch (e) {
				if (isCancelledError(e)) {
					return { wasSuccessful: false };
				}

				if (!tryProcessServerError(e, validationMessagesPrefix ?? "")) {
					alertManager.showError(localize(unhandledValidationMessageKey));
				}

				onIsBusyChange(false);
			}

			return { wasSuccessful: false };
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onIsBusyChange,
			openSsoApplicationCreatedDialog,
			tryProcessServerError
		]
	);

	return [
		submitHandler,
		verifyOAuth2Handler,
		enableMicrosoftAuthenticatorMobileSignInHandler,
		hasUnsavedOAuth2CredentialsChanges,
		<>
			{ssoApplicationCreatedDialog}
			{enableMicrosoftAuthenticatorMobileSignInDialog}
		</>
	];
}

export function convertToFormData(
	viewModel: SingleSignOnSettingsViewModel,
	isCustomSignOutRedirectUrlConfigurable: boolean
): SingleSignOnFormData {
	const {
		model,
		availableSingleSignOnTypes,
		isAutomaticSetupAvailable,
	} = viewModel;

	const {
		oAuth2ViewModel: oauth2,
		wsFederationViewModel: wsFed,
		signOutRedirectUrl
	} = model;

	const signOutRedirectUrlConfiguration: SignOutRedirectUrlConfiguration = {
		action: isCustomSignOutRedirectUrlConfigurable
			? signOutRedirectUrl
				? CustomSignOutRedirectUrlActionType.Enabled
				: CustomSignOutRedirectUrlActionType.Disabled
			: CustomSignOutRedirectUrlActionType.None,
		url: signOutRedirectUrl
	};

	return {
		singleSignOnType: model.singleSignOnType ?? availableSingleSignOnTypes[0],
		oauth2: {
			mode: model.singleSignOnType === SingleSignOnType.OAuth2 || !isAutomaticSetupAvailable
				? SingleSignOnConfigurationMode.Manual
				: SingleSignOnConfigurationMode.Automatic,
			automatic: {
				permissionToCreateAzureApplication: false,
				userProfilePermissions: false,
				sharePointPermissions: false,
				exchangePermissions: false,
				powerBiPermissions: false,
				windowsVirtualDesktopPermissions: false,
				windowsVirtualDesktopArmPermissions: false,
				shouldEnableMicrosoftAuthenticatorMobileSignIn: false,
				password: ""
			},
			manual: {
				authority: oauth2.authority,
				clientId: oauth2.clientId,
				key: {
					value: "",
					containsNewValue: false
				},
				domainHint: oauth2.domainHint,
				signOutRedirectUrlConfiguration,
				shouldSignOutWhenRefreshTokenExpires: oauth2.shouldSignOutWhenRefreshTokenExpires,
				wasVerifyClickedAtleastOnce: false,
				isVerified: false,
				doesKeyHaveValueOnServer: oauth2.keyContainsValue,
				isMicrosoftAuthenticatorMobileSignInEnabled: oauth2.isMobileAppMicrosoftAuthenticatorSignInEnabled
			}
		},
		wsFed: {
			metadataDocumentUrl: wsFed.metadataDocumentUrl,
			signOutRedirectUrlConfiguration
		}
	};
}

function createOAuth2ConfigurationSaveModel(formData: SingleSignOnFormData): OAuth2ConfigurationSaveModel {
	const {
		authority,
		clientId,
		key,
		domainHint,
		shouldSignOutWhenRefreshTokenExpires,
		signOutRedirectUrlConfiguration
	} = formData.oauth2
			.manual;

	return {
		authority,
		clientId,
		key,
		domainHint,
		shouldSignOutWhenRefreshTokenExpires,
		signOutRedirectUrlConfiguration
	};
}

function createOAuth2AzureApplicationCreationModel(
	formData: SingleSignOnFormData
): CreateOAuth2ApplicationInAzureModel {
	const {
		permissionToCreateAzureApplication,
		userProfilePermissions,
		sharePointPermissions,
		exchangePermissions,
		powerBiPermissions,
		windowsVirtualDesktopPermissions,
		windowsVirtualDesktopArmPermissions,
		shouldEnableMicrosoftAuthenticatorMobileSignIn,
		password,
	} = formData.oauth2
			.automatic;

	return {
		permissionToCreateAzureApplication,
		userProfilePermissions,
		sharePointPermissions,
		exchangePermissions,
		powerBiPermissions,
		windowsVirtualDesktopPermissions,
		windowsVirtualDesktopArmPermissions,
		shouldEnableMicrosoftAuthenticatorMobileSignIn,
		password
	};
}

function createWsFederationConfigurationSaveModel(
	formData: SingleSignOnFormData
): WsFederationConfigurationSaveModel {
	const {
		metadataDocumentUrl,
		signOutRedirectUrlConfiguration
	} = formData.wsFed;

	return {
		metadataDocumentUrl,
		signOutRedirectUrlConfiguration
	};
}
